<template>
  <div class="google-maps-address-picker">
    <ValidationProvider
      id="google_address"
      v-slot="{ errors }"
      name="google_address"
    >
      <v-text-field
        ref="autocompleteInput"
        v-model="selectedAddress"
        :error-messages="errors"
        label="Will Collection Address (search to autocomplete fields below)"
        @input="onAddressInput"
        outlined
        class="google-maps-address-picker-input"
      />
    </ValidationProvider>
  </div>
</template>

<script>
  export default {
    name: 'GoogleMapsAddressPicker',
    props: {
      value: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        selectedAddress: '',
        addressSelected: false,
        addressDetails: {
          streetNumber: '',
          streetName: '',
          suburb: '',
          city: '',
          province: '',
          postalCode: '',
          lat: null,
          lng: null,
        },
        autocomplete: null,
      };
    },
    watch: {
      value: {
        handler(newVal) {
          if (newVal && Object.keys(newVal).length > 0) {
            this.addressDetails = { ...newVal };
            this.addressSelected = true;
          }
        },
        immediate: true,
      },
    },
    mounted() {
      this.$nextTick(() => {
        this.initGoogleMaps();
      });
    },
    methods: {
      async initGoogleMaps() {
        if (!window.google || !window.google.maps) {
          console.log('Google Maps not available, loading script...');
          await this.loadGoogleMapsScript();
        }

        if (!window.google || !window.google.maps) {
          console.error(
            'Google Maps API is not available after loading attempt'
          );
          return;
        }

        this.setupAutocomplete();
      },

      loadGoogleMapsScript() {
        return new Promise(resolve => {
          if (
            window.google &&
            window.google.maps &&
            window.google.maps.places
          ) {
            resolve();
            return;
          }

          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
          script.async = true;
          script.defer = true;

          script.onload = () => {
            const checkReady = (attempts = 0) => {
              if (
                window.google &&
                window.google.maps &&
                window.google.maps.places
              ) {
                resolve();
              } else if (attempts < 50) {
                setTimeout(() => checkReady(attempts + 1), 100);
              } else {
                console.error(
                  'Google Maps Places API not available after loading'
                );
                resolve();
              }
            };
            checkReady();
          };

          script.onerror = error => {
            console.error('Failed to load Google Maps script:', error);
            resolve();
          };

          document.head.appendChild(script);
        });
      },

      setupAutocomplete() {
        let input = null;

        if (this.$refs.autocompleteInput && this.$refs.autocompleteInput.$el) {
          input = this.$refs.autocompleteInput.$el.querySelector('input');
          input.setAttribute('placeholder', '');
        }

        if (!input) {
          return;
        }

        try {
          this.autocomplete = new google.maps.places.Autocomplete(input, {
            componentRestrictions: { country: 'ZA' },
            types: ['address'],
          });

          this.autocomplete.addListener('place_changed', () => {
            this.handlePlaceSelected();
          });
        } catch (error) {
          console.error('Error setting up autocomplete:', error);
        }
      },

      handlePlaceSelected() {
        const place = this.autocomplete.getPlace();

        if (!place.geometry) {
          this.$emit('error', 'No details available for the selected address');
          return;
        }

        this.parseAddressComponents(place);

        this.addressDetails.lat = place.geometry.location.lat();
        this.addressDetails.lng = place.geometry.location.lng();

        this.addressSelected = true;
        this.emitAddressUpdate();
      },

      parseAddressComponents(place) {
        const addressComponents = place.address_components;

        this.addressDetails = {
          streetNumber: '',
          streetName: '',
          suburb: '',
          city: '',
          province: '',
          postalCode: '',
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        // Parse address components
        for (const component of addressComponents) {
          const types = component.types;

          if (types.includes('street_number')) {
            this.addressDetails.streetNumber = component.long_name;
          }

          if (types.includes('route')) {
            this.addressDetails.streetName = component.long_name;
          }

          if (types.includes('sublocality') || types.includes('neighborhood')) {
            this.addressDetails.suburb = component.long_name;
          }

          if (types.includes('locality')) {
            this.addressDetails.city = component.long_name;
          }

          if (types.includes('administrative_area_level_1')) {
            this.addressDetails.province = component.long_name;
          }

          if (types.includes('postal_code')) {
            this.addressDetails.postalCode = component.long_name;
          }
        }

        // If street name is empty, try to extract from formatted address
        if (!this.addressDetails.streetName && place.formatted_address) {
          const parts = place.formatted_address.split(',');
          if (parts.length > 0) {
            this.addressDetails.streetName = parts[0].trim();
          }
        }
      },

      onAddressInput() {
        if (!this.selectedAddress) {
          this.addressSelected = false;
          this.addressDetails = {
            streetNumber: '',
            streetName: '',
            suburb: '',
            city: '',
            province: '',
            postalCode: '',
            lat: null,
            lng: null,
          };
        }
        this.emitAddressUpdate();
      },

      emitAddressUpdate() {
        this.$emit('input', this.addressDetails);
        this.$emit('address-selected', this.addressDetails);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .google-maps-address-picker {
    width: 100%;
    position: relative;
  }

  .google-maps-address-picker-input {
    border-radius: 15px;
  }

  :deep(.google-maps-address-picker-input) {
    .v-input__control {
      .v-input__slot {
        padding-left: 10px !important;
      }
    }

    .v-label {
      padding-left: 10px !important;
      color: black;
      font-family: sans-serif;
    }

    &.v-input--is-focused,
    &.v-input--has-state {
      .v-input__control {
        .v-input__slot {
          padding-left: 10px !important;
        }
      }

      .v-label {
        padding-left: 10px !important;
      }
    }

    input {
      padding-left: 10px !important;
      color: black;
      font-family: sans-serif;
    }
  }

  .success-message {
    margin-top: 16px;
  }

  :global(.pac-container) {
    z-index: 9999 !important;
    position: absolute !important;
  }
</style>
